import NestedAccordion from "../../../components/NestedAccordion";

import FileUploadField from "@/components/ui/FileUploadField";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { getTimeDifference } from "@/utils/getTimeDifference";
import {
  EditingStageAdminRequiredActions,
  TVideoEditing,
} from "@/types/projectType";
import {
  useRequestVideoEditingTimeLineExtensionMutation,
  useUploadRevisedVideoEditingFileMutation,
  useUploadVideoEditingFileMutation,
} from "@/redux/feature/orderManagement/videoEditing/videoEditingApi";
import { FileDetails } from "@/types/types";
import { FileCard } from "@/pages/orderMananagement/components/FileCard";
import { RevisionFileCard } from "@/pages/orderMananagement/components/RevisionFileCard";
import { handleApiRequest } from "@/utils/handleApiRequest";
import { useGetDriveLinkQuery } from "@/redux/feature/driveLink/driveLinkApi";
import {
  DriveFolderLinkButton,
  DriveFolderMobileHint,
} from "@/components/ui/DriveFolderLinkButton";
import { useGlobalContext } from "@/context/GlobalContext";

const VideoEditing = ({
  videoEditingData,
  ActionsRequired,
  projectId,
}: {
  ActionsRequired?: EditingStageAdminRequiredActions[];
  videoEditingData: TVideoEditing;
  projectId: string;
}) => {
  const isActionRequired = ActionsRequired?.find(
    (item) =>
      item?.isVideoEditingUploaded === false ||
      item?.isRevisedVideoEditingUploaded === false,
  );
  const [searchParams] = useSearchParams();
  const { id: orderId } = useParams();
  const { userData } = useGlobalContext();
  const videoId = searchParams.get("videoId") || undefined;

  const timeLine = getTimeDifference(videoEditingData?.adminTimeLine);

  const [clearFiles, setClearFiles] = useState(false);
  const [file, setfile] = useState<FileDetails[]>([]);
  const [s3Uploading, setS3Uploading] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const [uploadVideoEditingFile, { isLoading: uploadingFile }] =
    useUploadVideoEditingFileMutation();
  const [uploadRevisedVideoEditingFile, { isLoading: uploadingRevisedFile }] =
    useUploadRevisedVideoEditingFileMutation();
  const [,] = useRequestVideoEditingTimeLineExtensionMutation();

  const { data, isFetching: driveLinkLoading } = useGetDriveLinkQuery(
    {
      email: userData?.email as string,
      projectId,
      videoId: videoId as string,
      type: "video_edit",
    },
    { skip: !userData?.email || !projectId || !videoId },
  );
  const driveLink = data?.data?.drive_link;

  const hasDirectFile = file.length > 0;
  const directUploadIncomplete = useMemo(
    () => hasDirectFile && file.some((f) => !f.live_url),
    [hasDirectFile, file],
  );
  const submitUsesDrive = !hasDirectFile && !s3Uploading;
  const driveNotReady = submitUsesDrive && (!driveLink || driveLinkLoading);

  const isMutationLoading = uploadingFile || uploadingRevisedFile;

  const handleSubmit = useCallback(async () => {
    if (!orderId || !videoId) {
      toast.error("Missing order/video context");
      return;
    }
    if (s3Uploading) {
      toast.error(
        "Finishing upload to our servers—submit will enable when the file is ready.",
      );
      return;
    }
    if (directUploadIncomplete) {
      toast.error(
        "Finishing upload to our servers—wait until the file shows as ready, then submit.",
      );
      return;
    }
    const useDrive = file.length === 0;
    if (useDrive) {
      if (driveLinkLoading) {
        toast.error("Preparing your Drive folder—please wait a moment.");
        return;
      }
      if (!driveLink) {
        toast.error(
          "Drive folder is not ready yet. Refresh the page or try again in a moment.",
        );
        return;
      }
    }

    if (videoEditingData?.requiredActions?.requiresRevision) {
      const payload = {
        orderId,
        videoId,
        data: {
          description: "Revised edited video",
          newVideoFile: useDrive ? [] : file,
          isUploadingWithDriveLink: useDrive,
          driveLink: driveLink,
        },
      };
      await handleApiRequest({
        payload,
        requestFunction: uploadRevisedVideoEditingFile,
        onSuccess: () => {
          toast.success("Edited video submitted successfully!");
          setfile([]);
          setClearFiles(true);
          setResetKey((k) => k + 1);
        },
        onError: (err) => {
          toast.error(
            (err as { data?: { message?: string }; message?: string })?.data
              ?.message ||
              (err as { message?: string })?.message ||
              "Failed to upload edited video",
          );
        },
      });
    } else {
      const versionNumber =
        (videoEditingData?.videoEditingVersions?.length ?? 0) + 1;
      const payload = {
        order_id: orderId,
        video_id: videoId,
        videoEditingVersions: [
          {
            versionNumber,
            uploadedBy: "user" as const,
            description:
              versionNumber === 1
                ? "First version of Edited Video"
                : `Version ${versionNumber}`,
            file: useDrive ? [] : file,
            isUploadingWithDriveLink: useDrive,
            driveLink: driveLink,
            uploadedAt: new Date(),
          },
        ],
      };

      await handleApiRequest({
        payload,
        requestFunction: uploadVideoEditingFile,
        onSuccess: () => {
          toast.success("Edited video uploaded successfully!");
          setfile([]);
          setClearFiles(true);
          setResetKey((k) => k + 1);
        },
        onError: (err) => {
          toast.error(
            (err as { data?: { message?: string }; message?: string })?.data
              ?.message ||
              (err as { message?: string })?.message ||
              "Failed to upload edited video",
          );
        },
      });
    }
  }, [
    directUploadIncomplete,
    driveLink,
    driveLinkLoading,
    file,
    orderId,
    uploadRevisedVideoEditingFile,
    uploadVideoEditingFile,
    videoEditingData?.requiredActions?.requiresRevision,
    videoEditingData?.videoEditingVersions?.length,
    videoId,
    s3Uploading,
  ]);

  const currentEditedVideoVersion =
    videoEditingData?.videoEditingVersions?.slice(-1)[0];
  const { inProgress = false, isApproved = false } =
    videoEditingData?.status || {};
  const { requiresRevision = false, requiresApproval = false } =
    videoEditingData?.requiredActions || {};

  const Status =
    requiresRevision === true
      ? "Needs Revision"
      : requiresApproval === true
        ? "Not Approved Yet"
        : inProgress === true
          ? " In Progress"
          : isApproved === true
            ? "Approved"
            : "";
  const [isOpen, setIsOpen] = useState(false);
  const showAllVersion = !isApproved && !requiresRevision;
  useEffect(() => {
    if (
      isActionRequired?.isRevisedVideoEditingUploaded === false ||
      isActionRequired?.isVideoEditingUploaded === false
    ) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [
    isActionRequired?.isRevisedVideoEditingUploaded,
    isActionRequired?.isVideoEditingUploaded,
  ]);

  return (
    <NestedAccordion
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      actionRequired={
        isActionRequired?.isRevisedVideoEditingUploaded === false ||
        isActionRequired?.isVideoEditingUploaded === false
      }
      title="Video Editing"
      initiallyOpen={!isApproved || requiresRevision}
      status={Status}
      isApproved={isApproved}
      timeLine={timeLine}
      initiated={videoEditingData ? true : false}
    >
      <div className="p-3">
        {!isApproved && !requiresApproval && (
          <div className="rounded-[10px]">
            <div id="pb-direct-upload-video-editing">
              <FileUploadField
                key={resetKey}
                clearFiles={clearFiles}
                onFilesUploaded={(f) => setfile(f)}
                onUploadingChange={setS3Uploading}
                maxFiles={5}
              />
            </div>
            <p className="text-center py-4"> or</p>
            <div className="w-full rounded-lg border bg-gray-50 flex flex-col items-center justify-center py-6 p-4">
              <p className="text-gray-700 text-sm mb-2">
                Upload your file to Google Drive
              </p>
              <p className="text-gray-500 text-xs text-center mb-3 max-w-md">
                Open the folder, upload your file, wait until it appears in
                Drive, then submit here. Only files in this folder count (not
                subfolders).
              </p>
              <DriveFolderMobileHint variant="upload" />
              {driveLinkLoading ? (
                <p className="text-sm text-gray-600">Preparing your folder…</p>
              ) : driveLink ? (
                <DriveFolderLinkButton
                  folderUrl={driveLink}
                  className="bg-blue-500 hover:text-white font-medium text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
                  variant="upload"
                  onPreferDirectUpload={() =>
                    document
                      .getElementById("pb-direct-upload-video-editing")
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      })
                  }
                />
              ) : (
                <p className="text-sm text-amber-700">
                  Could not load Drive link. Refresh the page.
                </p>
              )}
            </div>
          </div>
        )}

        {isApproved && (
          <>
            <p className="text-[13px] font-medium mt-4">Final Version</p>
            <FileCard
              active={true}
              file={currentEditedVideoVersion?.file[0]}
              isLink={!currentEditedVideoVersion?.file[0]}
              link={currentEditedVideoVersion?.link}
              isApproved={isApproved}
            />
          </>
        )}

        {requiresRevision && (
          <>
            <p className="text-[13px] font-medium mt-4">
              Request Revisions (
              {videoEditingData?.videoEditingVersions?.length})
            </p>
            <RevisionFileCard
              active={true}
              file={currentEditedVideoVersion?.file[0]}
              isLink={!currentEditedVideoVersion?.file[0]}
              link={currentEditedVideoVersion?.link}
              comment={currentEditedVideoVersion?.revisionComment as string}
              revisionImages={currentEditedVideoVersion?.revisionImages}
              revisionFeedbackItems={
                currentEditedVideoVersion?.revisionFeedbackItems
              }
            />
          </>
        )}

        <div>
          {videoEditingData?.videoEditingVersions
            ?.slice(0, showAllVersion ? undefined : -1)
            ?.reverse().length > 0 && (
            <div>
              <p className="text-[13px] font-medium mt-4">Previous Version</p>
              {videoEditingData?.videoEditingVersions
                ?.slice(0, showAllVersion ? undefined : -1)
                ?.reverse()
                .map((item, i) => (
                  <FileCard
                    requiresRevision={false}
                    key={i}
                    active={false}
                    file={item?.file[0]}
                    isLink={!item?.file[0]}
                    link={item?.link}
                  />
                ))}
            </div>
          )}
        </div>
      </div>
      <div className="bg-[#F5F9FF] p-3 rounded-bl-xl rounded-br-xl flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
        <p className="text-sm text-[#344054] font-medium">Cancel</p>
        <div className="flex flex-col items-end gap-1">
          {(s3Uploading || directUploadIncomplete) && (
            <p className="text-xs text-[#475467] text-right max-w-xs">
              Finishing upload to our servers—submit enables when the file is
              ready.
            </p>
          )}
          {driveNotReady && (
            <p className="text-xs text-[#475467] text-right">
              {driveLinkLoading
                ? "Preparing your Drive folder…"
                : "Open the Drive folder and upload, or wait for the link to load."}
            </p>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              !orderId ||
              !videoId ||
              isMutationLoading ||
              s3Uploading ||
              directUploadIncomplete ||
              driveNotReady
            }
            className="text-white disabled:opacity-50 text-sm px-3 py-2 font-semibold rounded-lg bg-[#17457D]"
          >
            {isMutationLoading
              ? "Uploading…"
              : s3Uploading
                ? "Finishing upload…"
                : "Submit upload"}
          </button>
        </div>
      </div>
    </NestedAccordion>
  );
};

export default VideoEditing;
